import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, FolderDown, Loader2, FileText } from "lucide-react";

import type { DocgenResponse } from "@/api/schemas";
import {
  assembleMapa1,
  previewMapa1,
  triggerDownload,
  type MapaAssemblePayload,
} from "@/api/client";
import { Button } from "@/components/ui/button";
import { countHr } from "@/lib/pluralHr";
import { useIntakeStore } from "@/features/project-data/useIntakeStore";
import { officePayload, sprancaRequestValue, useOfficeStore } from "@/features/projects/useOfficeStore";
import {
  placeholderFlagsByTitle,
  placeholderForTitle,
  placeholderLabel,
  placeholderTooltip,
  type PlaceholderInfo,
} from "./placeholderFlags";

/**
 * Ivo's ask: one bound PDF of the whole Mapa 1 — his uploads + our
 * generated documents, in order, with a Sadržaj whose page numbers match.
 *
 * F3: this is THE primary action of the Preuzmi screen — a hero card with
 * the download CTA always visible, the assembled Sadržaj underneath, and
 * every unfinished item ([ ZA RUČNI UNOS ] / plan nije u bazi) flagged both
 * here and on its document card below, so nothing incomplete slips into a
 * predaja unseen. The ordering / page-accuracy is the backend's job; this
 * is the window onto it.
 */
export function Mapa1Panel({
  projectId,
  data,
}: {
  projectId?: string;
  data: DocgenResponse;
}) {
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Build the assemble payload from the docgen result + the recognized
  // uploads (which carry the doc_ids the backend reads from disk).
  const payload = useMemo<MapaAssemblePayload>(() => {
    const recognized = projectId
      ? useIntakeStore.getState().get(projectId).recognizedDocs
      : [];
    return {
      izjave: data.izjave ?? [],
      naslovnice: data.naslovnice ?? [],
      tekstualni_dio: data.tekstualni_dio ?? [],
      tables: data.tables ?? [],
      mape: data.mape ?? [],
      recognized_docs: recognized as unknown as Array<Record<string, unknown>>,
      fact_sheet: data.fact_sheet ?? null,
      variables: data.opci_variables ?? null,
      office: officePayload(useOfficeStore.getState().office) ?? null,
      spranca: sprancaRequestValue(),
    };
    // Re-derive when the generated document changes; the intake store is
    // read at build time (it rarely changes on the Preuzmi screen).
  }, [projectId, data]);

  const preview = useQuery({
    queryKey: ["mapa1-preview", projectId, data.izjave.length, data.tekstualni_dio.length],
    queryFn: () => previewMapa1(payload),
    retry: 1,
    staleTime: 30_000,
  });

  async function download() {
    setDownloading(true);
    setError(null);
    try {
      const blob = await assembleMapa1(payload);
      triggerDownload(blob, "Permitko_Mapa_1.pdf");
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setDownloading(false);
    }
  }

  const sadrzaj = preview.data?.sadrzaj ?? [];
  const totalPages = preview.data?.page_count ?? 0;

  // F3 — unfinished-work flags, derived from the SAME sections the payload
  // binds (the Sadržaj API carries only titles/pages; the API stays as-is).
  const flags = useMemo(
    () =>
      placeholderFlagsByTitle([
        ...(data.izjave ?? []),
        ...(data.naslovnice ?? []),
        ...(data.tekstualni_dio ?? []),
        ...(data.tables ?? []),
        ...(data.mape ?? []),
      ]),
    [data],
  );
  const flaggedCount = useMemo(() => {
    let n = 0;
    for (const e of sadrzaj) {
      if (placeholderForTitle(flags, e.title)) n += 1;
      for (const s of e.sub_entries ?? []) {
        if (placeholderForTitle(flags, s.title)) n += 1;
      }
    }
    return n;
  }, [sadrzaj, flags]);

  // Group entries in bound order (the backend already returns them ordered).
  const grouped = useMemo(() => {
    const out: { group: string | null; entries: typeof sadrzaj }[] = [];
    for (const e of sadrzaj) {
      const last = out[out.length - 1];
      if (last && last.group === e.group) last.entries.push(e);
      else out.push({ group: e.group, entries: [e] });
    }
    return out;
  }, [sadrzaj]);

  return (
    <section className="overflow-hidden rounded-lg border border-ind-300 bg-white shadow-sm">
      {/* Hero header — the screen's primary action, CTA always visible. */}
      <div className="flex flex-wrap items-center gap-3 border-b border-ind-100 bg-ind-50/60 px-5 py-4">
        <FolderDown className="h-5 w-5 shrink-0 text-ind-700" />
        <div className="min-w-0 flex-1">
          <h2 className="text-[1rem] font-bold leading-tight text-stone-800">
            Cijela Mapa 1 — jedan uvezani PDF
          </h2>
          <p className="text-[0.8rem] text-stone-500">
            Tvoji prilozi + generirani dokumenti, po redu, sa sadržajem čije
            stranice vode točno na svaki dokument.
            {preview.isLoading ? (
              <span className="ml-1.5 inline-flex items-center gap-1 text-stone-400">
                <Loader2 className="h-3 w-3 animate-spin" /> slažem sadržaj…
              </span>
            ) : sadrzaj.length > 0 ? (
              <span className="ml-1.5">
                {countHr(sadrzaj.length, "dokument", "dokumenta", "dokumenata")} ·{" "}
                {totalPages} str.
              </span>
            ) : null}
          </p>
        </div>
        <Button
          type="button"
          size="lg"
          onClick={download}
          disabled={downloading || preview.isLoading}
        >
          {downloading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" /> Slažem PDF…
            </>
          ) : (
            <>
              <FolderDown className="h-4 w-4" /> Preuzmi cijelu Mapu 1 (PDF)
            </>
          )}
        </Button>
      </div>

      <div className="px-5 py-4">
        {/* Unfinished work, said ONCE up front — the same flags mark each
            row below and each document card in the per-document view. */}
        {flaggedCount > 0 && (
          <div className="mb-3 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[0.82rem] text-amber-900">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>
              {flaggedCount === 1
                ? "1 stavka u Mapi nosi oznaku nedovršenog sadržaja ([ ZA RUČNI UNOS ] ili plan nije u bazi) — dopuni je u pregledu niže prije predaje."
                : `${flaggedCount} stavki u Mapi nosi oznaku nedovršenog sadržaja ([ ZA RUČNI UNOS ] ili plan nije u bazi) — dopuni ih u pregledu niže prije predaje.`}{" "}
              PDF se svejedno može preuzeti.
            </span>
          </div>
        )}

        {preview.isError && (
          <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-2.5 text-[0.82rem] text-amber-800">
            Ne mogu složiti pregled sadržaja — provjeri jesu li dokumenti
            generirani i prilozi učitani.
          </div>
        )}

        {sadrzaj.length > 0 && (
          <div className="mb-4 overflow-hidden rounded-md border border-stone-200">
            <div className="border-b border-stone-100 bg-stone-50 px-3 py-2 text-[0.72rem] font-bold uppercase tracking-[0.04em] text-stone-500">
              Sadržaj Mape 1
            </div>
            <ul className="divide-y divide-stone-50">
              {grouped.map((g, gi) => (
                <li key={gi}>
                  {g.group && (
                    <div className="bg-white px-3 pt-2.5 pb-1 text-[0.74rem] font-bold uppercase tracking-[0.03em] text-ind-700">
                      {g.group}
                    </div>
                  )}
                  <ul>
                    {g.entries.map((e, i) => (
                      <li key={i}>
                        <div className="flex items-baseline gap-2 px-3 py-1.5 text-[0.84rem] text-stone-700">
                          <FileText className="h-3.5 w-3.5 shrink-0 translate-y-0.5 text-stone-300" />
                          {e.oznaka && (
                            <span className="font-mono text-[0.76rem] text-stone-400">
                              {e.oznaka}
                            </span>
                          )}
                          <span className="min-w-0 flex-1 truncate">{e.title}</span>
                          <PlaceholderChip
                            info={placeholderForTitle(flags, e.title)}
                          />
                          <span className="tabular-nums text-[0.76rem] text-stone-400">
                            str. {e.start_page}
                          </span>
                        </div>
                        {(e.sub_entries ?? []).length > 0 && (
                          <ul>
                            {(e.sub_entries ?? []).map((s, si) => (
                              <li
                                key={si}
                                className="flex items-baseline gap-2 py-1 pl-11 pr-3 text-[0.8rem] text-stone-500"
                              >
                                {s.oznaka && (
                                  <span className="font-mono text-[0.72rem] text-stone-400">
                                    {s.oznaka}
                                  </span>
                                )}
                                <span className="min-w-0 flex-1 truncate">{s.title}</span>
                                <PlaceholderChip
                                  info={placeholderForTitle(flags, s.title)}
                                />
                                <span className="tabular-nums text-[0.72rem] text-stone-400">
                                  str. {s.start_page}
                                </span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          </div>
        )}

        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 px-4 py-2 text-[0.82rem] text-red-700">
            {error}
          </div>
        )}
      </div>
    </section>
  );
}

/** Amber "not done" chip on a Sadržaj row; nothing when the item is clean. */
function PlaceholderChip({ info }: { info: PlaceholderInfo | null }) {
  if (!info) return null;
  return (
    <span
      className="inline-flex shrink-0 items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-2 py-px text-[0.68rem] font-semibold text-amber-800"
      title={placeholderTooltip(info)}
    >
      <AlertTriangle className="h-2.5 w-2.5" />
      {placeholderLabel(info)}
    </span>
  );
}
